import {DateTime} from 'luxon';
import {FeedRuleType} from './locationFeed.enums';
import {ScheduledJobStatus, ScheduledJobTiming} from '../jobs/jobs.types';
import {SourceConfig} from '../feeds/feeds.types';
import {CampaignUsage} from '../campaigns/campaigns.types';

export interface CampaignLocationFeed {
  campaignId: number;
  locationFeed?: LocationFeed;
  locationFeedId: number;
  distanceUnit?: number;
  locationTargetingMethod: number;
  maxDistFallbackOption: number;
  maxDistKm: number;
  requireProfile?: boolean;
  topLocationIds: string[];
}

export interface RemoteFeedMapping {
  allowComments: boolean;
  colNameMap: Record<string, string>;
  dateFormat: string;
  delimiterName: string;
  escapeCharName: string;
  exclusionsFileUrl: string;
  inclusionsFileUrl: string;
  itemXmlNodeName: string;
  nameSpace: string;
  quotatorName: string;
  rules: RemoteFeedMappingRule[];
}

export interface FeedMapping extends Omit<RemoteLocationFeed['mapping'], 'rules'> {
  rules: FeedMappingRule[];
}

export interface RemoteLocationFeed {
  id: number;
  itemCount: number;
  lastUploadDate: string;
  createTime?: string;
  createdByUserName?: string;
  name: string;
  index: number;
  campaignsIds: number[];
  feedType: number;
  feedUploadMethod: number;
  fileUrl: string;
  mapping: RemoteFeedMapping;
  fileMetadata: {
    columnNameToValuesExamplesMap: Record<string, string[]>;
  };
  usedInCampaigns: CampaignUsage[];
  isRemoveAbsentItems: boolean;
  issues: {
    hasContent: boolean;
    hasErrors: boolean;
    hasWarning: boolean;
    hasInfos: boolean;
    items: [];
  };
  topLocationIds: number[];
  rectSizeKm: number;
  scheduledJob?: {
    status: ScheduledJobStatus;
    timing?: ScheduledJobTiming;
  };
  sourceConfig?: SourceConfig;
}

export interface LocationFeed
  extends Omit<RemoteLocationFeed, 'lastUploadDate' | 'createTime' | 'mapping'> {
  lastUploadDate: DateTime;
  createTime?: DateTime;
  mapping: FeedMapping;
}

export enum LocationTargetingMethods {
  NearestLocation = 'NearestLocation',
  ByZipCode = 'ByZipCode',
  ByZipCodeFromDsp = 'ByZipCodeFromDspOnly',
  ByDMA = 'ByDMA',
  ByPlacement = 'ByPlacement',
  SpecificStore = 'SpecificStore',
}

export enum LocationTargetingMethodValue {
  NearestLocation = 0,
  ByZipCode = 10,
  ByZipCodeFromDsp = 12,
  ByDMA = 20,
  ByPlacement = 21,
  SpecificStore = 30,
}

export interface FeedMappingDictionaryItem {
  basicInfo: string;
  exampleValues: string[];
  expectedFormat: string;
  fullName: string;
  index: number;
  isRequired: boolean;
  requirements: string;
  shortName: string;
  taxonomyKey: string;
}

export type FeedMappingDictionary = Record<string, FeedMappingDictionaryItem>;

export interface RemoteFeedMappingRule {
  capitalization: number;
  columnName: string;
  defaultValue: string;
  overrideValue: string;
  regexExtract: string;
  regexReplace: {Key: string; Value: string}[];
  replace: {Key: string; Value: string}[];
  removeBackground: boolean;
}

export interface FeedMappingRule extends Omit<RemoteFeedMappingRule, 'replace' | 'regexReplace'> {
  replace: {key: string; value: string}[];
  regexReplace: {key: string; value: string}[];
  ruleType: FeedRuleType;
}
